import { computed, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { BusinessState } from '@state/business';
import { BusinessDispatch } from '@state/business';
import {
	DateLineLocationFormat,
	DateLineDateFormat,
	DateLinePrepositionFormat,
	DatedTextFormat,
	DateLineFormatSettings,
} from '@models/businessSettings/settings';

export class DateLineFormatViewModel {
	private store = inject(Store);
	private businessDispatch = inject(BusinessDispatch);

	// Get settings from store
	dateLineFormat = this.store.selectSignal(
		BusinessState.getDefaultDateLineFormatSettings,
	);

	// Dropdown items
	datedTextFormatItems = [
		{ value: DatedTextFormat.TITLE_CASE, name: 'Title case' },
		{ value: DatedTextFormat.TITLE_CASE_BOLD, name: 'Title case bold' },
		{ value: DatedTextFormat.CAPITALIZED, name: 'Capitalized' },
		{ value: DatedTextFormat.CAPITALIZED_BOLD, name: 'Capitalized bold' },
	];

	// Hardcoded values for preview
	private city = 'Toronto';
	private province = 'Ontario';
	private hardcodedDate = '27th day of April, 2025';
	private hardcodedShortDate = 'April 27, 2025';

	// Computed property for the preview text
	previewText = computed(() => {
		const location = this.getLocationText();
		const dated = this.getDatedText();
		const dateText = this.getDateText();
		const preposition = this.getPrepositionText();

		let text = `${dated}`;

		if (this.dateLineFormat()?.signedRemotely) {
			text += ' remotely';
		}

		text += ` ${location}`;

		if (this.dateLineFormat().addComma) {
			text += ',';
		}

		// Only add preposition if date format is long
		if (this.dateLineFormat().dateFormat === DateLineDateFormat.LONG) {
			text += ` ${preposition} ${dateText}`;
		} else {
			text += ` ${dateText}`;
		}

		return text;
	});

	// Helper methods for generating parts of the preview text
	private getLocationText(): string {
		switch (this.dateLineFormat().locationFormat) {
			case DateLineLocationFormat.LONG:
				return `at the City of ${this.city}, in the Province of ${this.province}`;
			case DateLineLocationFormat.MEDIUM:
				return `at ${this.city}, ${this.province}`;
			case DateLineLocationFormat.SHORT:
			default:
				return `at ${this.city}`;
		}
	}

	private getDatedText(): string {
		switch (this.dateLineFormat().datedTextFormat) {
			case DatedTextFormat.TITLE_CASE:
				return 'Dated';
			case DatedTextFormat.TITLE_CASE_BOLD:
				return '<strong>Dated</strong>';
			case DatedTextFormat.CAPITALIZED:
				return 'DATED';
			case DatedTextFormat.CAPITALIZED_BOLD:
				return '<strong>DATED</strong>';
			default:
				return 'Dated';
		}
	}

	private getDateText(): string {
		return this.dateLineFormat().dateFormat === DateLineDateFormat.LONG
			? this.hardcodedDate
			: this.hardcodedShortDate;
	}

	private getPrepositionText(): string {
		return this.dateLineFormat().prepositionFormat ===
			DateLinePrepositionFormat.ON_THIS
			? 'on this'
			: 'this';
	}

	// Get display name for datedTextFormat
	getDatedTextFormatName(format: DatedTextFormat): string {
		const item = this.datedTextFormatItems.find(
			(item) => item.value === format,
		);
		return item ? item.name : 'Title case';
	}

	// Event handlers
	onLocationFormatChange(format: DateLineLocationFormat): void {
		this.saveSetting('locationFormat', format);
	}

	onTextFormatChange(format: DatedTextFormat): void {
		this.saveSetting('datedTextFormat', format);
	}

	onDateFormatChange(format: DateLineDateFormat): void {
		this.saveSetting('dateFormat', format);
	}

	onPrepositionFormatChange(format: DateLinePrepositionFormat): void {
		this.saveSetting('prepositionFormat', format);
	}

	onAddCommaChange(checked: boolean): void {
		this.saveSetting('addComma', checked);
	}

	onSignedRemotelyChange(checked: boolean): void {
		this.saveSetting('signedRemotely', checked);
	}

	// Helper method to save a setting
	private saveSetting(
		fieldName: keyof DateLineFormatSettings,
		value: DateLineFormatSettings[keyof DateLineFormatSettings],
	): void {
		this.businessDispatch.OverwriteBusinessFields(
			`defaultSettings.dateLineFormat.${fieldName}`,
			value,
		);
	}
}
